import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import type { Props as DefaultLegendContentProps } from "recharts/types/component/DefaultLegendContent";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import { toTitle } from "@/utils/case";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { useRecoilState } from "recoil";
import { useSearchParams } from "react-router-dom";
import { Company } from "@/features/types";
import { useGetMarkupTrendBrandList } from "@/features/dashboard/features/markup-trend-section/hooks/useGetMarkupTrendBrandList";
import MarkupRequestComponent from "@/features/dashboard/features/markup-trend-section/components/markup-request-component";
import MarkupRequestModal from "@/features/dashboard/features/markup-trend-section/components/markup-request-modal";
import { removeObjDuplicateBy } from "@/features/dashboard/utils";
import { markupSectionRequestModalStore } from "@/features/dashboard/features/markup-trend-section/store/markup-section-store";
import useIsOverflow from "@/hooks/useIsOverflow";

interface Props extends DefaultLegendContentProps {
  colors: {
    lineColor: string;
    legendBgColor: string;
    legendTxtColor: string;
  }[];
  outerOpen?: boolean;
  setOuterOpen?: (open: boolean) => void;
}

const CustomLegend = ({ payload, colors, outerOpen, setOuterOpen }: Props) => {
  const [isOpen, setIsOpen] = useRecoilState(markupSectionRequestModalStore);
  const [searchParams, setSearchParams] = useSearchParams();

  const uniquePayload = useMemo(
    () =>
      removeObjDuplicateBy(payload ?? [], "dataKey")
        .filter((item) => item.value.includes("_solid"))
        .map((item) => ({
          ...item,
          value: item.value.replace("_solid", ""),
        })),
    [payload]
  );

  if (!payload || payload.some((item) => item.value.includes("undefined")))
    return null;

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        paddingLeft: 23,
        marginLeft: 12,
        width: "104px",
        gap: "4px",
        transform: "translateY(-15px)",
      }}
    >
      {uniquePayload.map((entry, index) => (
        <CustomLegendItem
          value={entry.value}
          key={entry.value + index}
          index={index}
          colors={colors}
        />
      ))}

      <MarkupRequestModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </Box>
  );
};

const areEqual = (
  prev: DefaultLegendContentProps,
  next: DefaultLegendContentProps
) => {
  if (!prev.payload || !next.payload) return prev.payload === next.payload;
  if (prev.payload.length !== next.payload.length) return false;
  return prev.payload.every(
    (ent, i) =>
      ent.value === next.payload![i].value &&
      ent.dataKey === next.payload![i].dataKey
  );
};

interface ItemProps {
  value: string;
  index: number;
  colors: Props["colors"];
}

const CustomLegendItem = ({ value, index, colors }: ItemProps) => {
  const { ref, isOverflow } = useIsOverflow<HTMLParagraphElement>();

  return (
    <Tooltip
      key={value}
      label={isOverflow ? value : ""}
      borderRadius={"8px"}
      opacity={0.9}
      background={"#363636"}
      color={"#FFFFFF"}
      padding={"8px 12px"}
    >
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          background: colors[index].legendBgColor,
          maxWidth: "100px",
          width: "fit-content",
          borderRadius: "4px",
          padding:
            "clamp(2px, calc(-2.93px + 0.3610vmin), 4px) " +
            "clamp(6px, calc(1.07px + 0.3610vmin), 8px)",
          color: colors[index].legendTxtColor,
          paddingLeft: "0px",
          height: "clamp(22px, calc(11.2px + 1.083vmin), 32px)",
        }}
      >
        <Box>
          <SVG src={"/icons-v1_1/dot.svg"} />
        </Box>
        <Text
          ref={ref}
          textStyle={"caption-medium"}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginRight: "8px",
          }}
        >
          {value}
        </Text>
      </Box>
    </Tooltip>
  );
};

export default React.memo<Props>(CustomLegend, areEqual);
